import React from 'react';
import { ByzzerCategorySelect } from '@/components/ByzzerCategorySelect';
import { ByzzerWindowedSelect, ByzzerButton } from '@byzzer/ui-components';
import { CharacteristicsDimensionSelect } from '@/components/CharacteristicsDimensionSelect/CharacteristicsDimensionSelect';
import { FilterState, FilterOptions } from '../hooks/useItemCodingExplorerFilters';
import { PRODUCT_LEVEL_OPTIONS } from '../constants/productLevelOptions';
import './FilterPanel.scss';
import { ByzzerPPGGroupPicker } from '@/components/ByzzerPPGGroupPicker';
import classNames from 'classnames';

interface FilterPanelProps {
    filters: FilterState;
    filterOptions: FilterOptions;
    filterOptionsLoading?: boolean;
    onFilterChange: (e: any) => void;
    onApply: () => void;
    expanded?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
    filters,
    filterOptions,
    filterOptionsLoading = false,
    onFilterChange,
    onApply,
    expanded = true
}) => {
    // Validation logic for mandatory fields
    const isCategoryValid = filters?.categories?.length > 0;
    const isProductLevelValid = filters?.productLevel?.length > 0;
    const isCharacteristicsValid = filters?.productDimensions?.length > 0;
    
    const isFormValid = isCategoryValid && isProductLevelValid && isCharacteristicsValid;
    
    const handleApplyClick = () => {
        if (isFormValid) {
            onApply();
        }
    };

    const handleUpcChange = (e: ByzzerChangeEvent<string[]>) => {
        onFilterChange({
            name: 'upcs',
            value: e.value.flat() ?? [],
        })
    }

    return (
        <div className={classNames("wrapper-content", {
            'wrapper-content--hidden': !expanded
        })}>
            <div className="filter-section">
                <h3 className="filter-section__title">Filter Your Products</h3>
                <div className="filter-section__filters">
                    <div className="filter-group">
                        <ByzzerCategorySelect
                            className="category-select"
                            name={'categories'}
                            label={'Category *'}
                            value={filters.categories}
                            onChange={onFilterChange}
                            placeholder={'Select from the list'}
                            allowClear={true}
                            allowMultiple={true}
                            categorySelectionAggregationLevel={'category'}
                        />
                    </div>
                    <div className="filter-group">
                        <ByzzerWindowedSelect
                            className="parent-company-select"
                            name={'parentCompanies'}
                            label={'Parent Company'}
                            options={filterOptions.parentCompanies}
                            value={filters.parentCompanies}
                            onChange={onFilterChange}
                            placeholder={filterOptionsLoading ? 'Loading...' : 'Select parent companies'}
                            allowClear={true}
                            allowMultiple={true}
                            disabled={filterOptionsLoading}
                        />
                    </div>
                    <div className="filter-group">
                        <ByzzerWindowedSelect
                            className="manufacturer-select"
                            name={'manufacturers'}
                            label={'Manufacturer'}
                            options={filterOptions.manufacturers}
                            value={filters.manufacturers}
                            onChange={onFilterChange}
                            placeholder={filterOptionsLoading ? 'Loading...' : 'Select manufacturers'}
                            allowClear={true}
                            allowMultiple={true}
                            disabled={filterOptionsLoading}
                        />
                    </div>
                    <div className="filter-group">
                        <ByzzerWindowedSelect
                            className="brands-select"
                            name={'brands'}
                            label={'Brand'}
                            options={filterOptions.brands}
                            value={filters.brands}
                            onChange={onFilterChange}
                            placeholder={filterOptionsLoading ? 'Loading...' : 'Select brands'}
                            allowClear={true}
                            allowMultiple={true}
                            disabled={filterOptionsLoading}
                        />
                    </div>
                    <div className="filter-group">
                        <ByzzerPPGGroupPicker
                            className="upcs-select"
                            name={'upcs'}
                            label={'UPC'}
                            value={filters.upcs}
                            onChange={handleUpcChange}
                            placeholder={'Select UPCs'}
                            allowClear={true}
                            allowMultiple={true}
                            ppgId={-2}
                            maxSelections={Infinity}
                            showDescription={false}
                            categories={filters.categories ?? []}
                            brands={filters.brands ?? []}
                            parentCompanies={filters.parentCompanies ?? []}
                            manufacturers={filters.manufacturers ?? []}
                        />
                    </div>
                </div>

                <div className="aggregate-section">
                    <h3 className="aggregate-section__title">Aggregate Products Across</h3>
                    <div className="aggregate-group">
                        <ByzzerWindowedSelect
                            className="aggregate-select"
                            name={'productLevel'}
                            label={'Product Level *'}
                            options={PRODUCT_LEVEL_OPTIONS}
                            value={filters.productLevel}
                            onChange={onFilterChange}
                            placeholder={'Select product level'}
                            allowClear={true}
                            allowMultiple={false}
                        />
                    </div>
                </div>

                <div className="characteristics-section">
                    <div className="characteristics-group">
                        <CharacteristicsDimensionSelect
                            name={'productDimensions'}
                            categories={filters.categories}
                            onChange={onFilterChange}
                            value={filters.productDimensions}
                            maxSelections={1}
                            required={true}
                            includeCustomCharacteristics={false}
                        />
                    </div>
                </div>

                <div className="filter-section__actions">
                    <ByzzerButton 
                        className="filter-action-btn filter-action-btn--apply" 
                        onClick={handleApplyClick}
                        disabled={!isFormValid}
                        disabledTip={!isFormValid ? 'Please fill all mandatory fields: Category, Product Level, and a Characteristic' : ''}
                    >
                        Apply
                    </ByzzerButton>
                </div>
            </div>
        </div>
    );
};
